import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, X } from "lucide-react";
import { fetchUrgency } from "@/lib/urgency";

const DISMISS_KEY = "pcs_urgency_dismissed";
export const URGENCY_UPDATED = "pcs-urgency-updated";
const pad = (n) => String(n).padStart(2, "0");

export function useCountdown(endAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = endAt ? new Date(endAt).getTime() - now : 0;
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 };
}

const Unit = ({ value, label, testId }) => (
  <span className="inline-flex items-baseline gap-0.5" data-testid={testId}>
    <span className="font-display text-base md:text-lg tabular-nums leading-none">{value}</span>
    <span className="font-mono text-[9px] opacity-70">{label}</span>
  </span>
);

export default function UrgencyBanner({ preview = false }) {
  const [cfg, setCfg] = useState(null);
  const [dismissedKey, setDismissedKey] = useState(() => sessionStorage.getItem(DISMISS_KEY));
  const left = useCountdown(cfg?.end_at);

  useEffect(() => {
    const load = (e) => fetchUrgency(Boolean(e)).then(setCfg);
    load();
    window.addEventListener(URGENCY_UPDATED, load);
    return () => window.removeEventListener(URGENCY_UPDATED, load);
  }, []);

  const campaignKey = `${cfg?.name}|${cfg?.end_at}`;
  if (!cfg?.enabled || !left || (!preview && dismissedKey === campaignKey)) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, campaignKey);
    setDismissedKey(campaignKey);
  };

  return (
    <div className="bg-[#0A0A0A] text-white border-b-2 border-black pcs-urgency-in" data-testid="urgency-banner">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex items-center gap-3 md:gap-5">
        <Clock className="w-4 h-4 text-[#FF9900] shrink-0 pcs-tick" strokeWidth={3} />
        <p className="text-xs md:text-sm font-bold truncate" data-testid="urgency-banner-message">{cfg.message}</p>
        <div className="ml-auto flex items-center gap-2 md:gap-3 shrink-0" data-testid="urgency-banner-countdown">
          <Unit value={left.d} label="D" testId="urgency-days" />
          <Unit value={pad(left.h)} label="H" testId="urgency-hours" />
          <Unit value={pad(left.m)} label="M" testId="urgency-minutes" />
          <span className="hidden sm:inline-flex"><Unit value={pad(left.s)} label="S" testId="urgency-seconds" /></span>
        </div>
        {cfg.link_url && cfg.link_label && (
          <Link
            to={cfg.link_url}
            data-testid="urgency-banner-link"
            className="inline-block pcs-bg-pink text-white border-2 border-white px-2 md:px-3 py-0.5 md:py-1 font-mono uppercase text-[9px] md:text-[10px] tracking-widest whitespace-nowrap hover:bg-white hover:text-black transition-colors"
          >
            {cfg.link_label}
          </Link>
        )}
        {!preview && (
          <button onClick={dismiss} aria-label="Dismiss" data-testid="urgency-banner-dismiss" className="opacity-60 hover:opacity-100 transition-opacity">
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        )}
      </div>
    </div>
  );
}
